import { useMemo } from 'react';
import type {
  CustomThemeDecoration,
  CustomThemeDecorationSpec,
  DecorationSprite,
  DecorationSpec,
} from '../types';
import { DecorationLayer } from './DecorationLayer';

// tasks/46 §D5 — render a family CustomTheme's decoration where each
// sprite maps to its own `<DecorationLayer>` mount.
//
// - `pickMode: 'all'` composes one layer per sprite. Density is shared
//   across layers so the total sprite count stays comparable to a
//   single-sprite theme at the same density level.
// - `pickMode: 'random'` picks one sprite deterministically per
//   `(routineId, themeKey)` so a kid seeing the same routine with the
//   same theme always gets the same decoration during a session — no
//   mid-tap swaps.
interface DecorationSpecFromCustomProps {
  readonly decoration: CustomThemeDecoration;
  readonly themeKey: string;
  readonly routineId?: string;
}

export function DecorationSpecFromCustom({
  decoration,
  themeKey,
  routineId,
}: DecorationSpecFromCustomProps) {
  const specs = useMemo<DecorationSpec[]>(
    () => decorationSpecs(decoration, themeKey, routineId),
    [decoration, themeKey, routineId],
  );
  if (specs.length === 0) return null;
  return (
    <>
      {specs.map((spec, i) => (
        <DecorationLayer key={`${spec.sprite}-${i}`} spec={spec} />
      ))}
    </>
  );
}

function decorationSpecs(
  decoration: CustomThemeDecoration,
  themeKey: string,
  routineId: string | undefined,
): DecorationSpec[] {
  if (decoration.kind === 'off') return [];
  if (decoration.sprites.length === 0) return [];

  if (decoration.pickMode === 'random') {
    const seed = `${routineId ?? ''}:${themeKey}`;
    const idx = pickIndex(seed, decoration.sprites.length);
    const sprite = decoration.sprites[idx]!;
    return [buildSpec(sprite, decoration, decoration.density)];
  }

  const shared = shareDensity(decoration.density, decoration.sprites.length);
  return decoration.sprites.map((sprite) => buildSpec(sprite, decoration, shared));
}

// Downgrade the requested density level per layer so a 3-sprite 'high'
// theme doesn't ship 60 sprites on screen. Single-sprite themes keep
// the requested density verbatim so the picker stays predictable when
// the user isn't multi-selecting.
function shareDensity(
  density: CustomThemeDecorationSpec['density'],
  n: number,
): CustomThemeDecorationSpec['density'] {
  if (n <= 1) return density;
  if (n === 2) return density === 'high' ? 'med' : 'low';
  return 'low';
}

function buildSpec(
  sprite: DecorationSprite,
  decoration: CustomThemeDecorationSpec,
  density: CustomThemeDecorationSpec['density'],
): DecorationSpec {
  const spec: DecorationSpec = {
    sprite,
    motion: decoration.motion,
    density,
    speed: decoration.speed,
  };
  if (decoration.colors && decoration.colors.length > 0) {
    spec.colors = decoration.colors;
  }
  return spec;
}

// FNV-1a-ish string hash. Stable across sessions, deterministic given
// the same seed. Only used to pick a sprite index — good enough
// distribution for our tiny (≤8) sprite arrays.
function pickIndex(seed: string, mod: number): number {
  let h = 2166136261;
  for (const ch of seed) {
    h ^= ch.codePointAt(0) ?? 0;
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h) % mod;
}
